import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { connect } from 'react-redux';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { getSystemDetails } from '../../stores/actions/installerActions';
import MapLoading from '../Dashboard/MapLoading';

const DEFAULT_HTTP_PORT = 80;
const DEFAULT_HTTPS_PORT = 443;
const DEFAULT_SSH_PORT = 22;

interface LabeledValue {
  name: string;
  value: number | null;
}

interface BatteryLevel {
  min: number | null;
  values: LabeledValue[];
}

interface Deflections {
  max: number | null;
  values: LabeledValue[];
}

interface GatewayDetails {
  node_id: number;
  firmware_version: string | null;
  sms_simcard_telephone: string | null;
  sms_simcard_owner: string | null;
  sms_simcard_pin: string | null;
  sms_simcard_puk: string | null;
  sms_simcard_network: string | null;
  data_simcard_telephone: string | null;
  data_simcard_ip: string | null;
  data_simcard_owner: string | null;
  data_simcard_pin: string | null;
  data_simcard_puk: string | null;
  data_simcard_network: string | null;
  device_sim_pin: string | null;
  router_serial: string | null;
  router_owner: string | null;
  router_supplier: string | null;
  supply_voltage: number | null;
  vpn2_ip: string | null;
  web_server_config: {
    w2_cfg_dhcp: boolean | null;
    w2_cfg_ip: string | null;
    w2_cfg_netmask: string | null;
    w2_cfg_gateway: string | null;
    w2_cfg_dns1: string | null;
    w2_cfg_dns2: string | null;
  };
}

interface SystemDetailsData {
  id: number;
  systemVersion: string;
  name: string;
  externalSystemId?: string;
  externalSynchronizationAt?: string;
  w2PublicIpAddress?: string;
  w2HttpPort?: number;
  w2HttpsPort?: number;
  w2SshPort?: number;
  gateways: GatewayDetails[] | null;
  batteryLevel: BatteryLevel;
  deflections: Deflections;
  guaranteeAgreementEndDate?: string;
  handoverDate?: string;
  baseGuaranteeTime?: number;
  maintenanceCoveredByGuarantee?: boolean;
  additionalNotes?: string;
  obstructionDetectionThreshold?: number;
  obstructionChangeThreshold?: number;
  obstructionCancelThreshold?: number;
  inclinometerRange?: number;
  internetCheckHost?: string;
  angleChangeThreshold?: number;
  systemIdForRadioSet?: number[] | null;
  w2ModbusEnabled?: boolean;
  latitude?: number;
  longitude?: number;
  w2RelayOnThreshold?: number;
  buyerName?: string;
  servicePurchaserName?: string;
}

interface SystemDetailsProps {
  mobileView: boolean;
  systemDetails: SystemDetailsData | null;
  getSystemDetails: (sysId: number) => void;
}

interface Row {
  label: string;
  value: React.ReactNode;
}

const renderLabeledSummary = (
  headline: number | null | undefined,
  values: LabeledValue[] | undefined,
  suffix = '%'
) => {
  if (!values || values.length === 0) return null;
  return (
    <span>
      {headline ?? '?'}
      {suffix} (
      {values.map((v, i) => (
        <span key={v.name}>
          {i > 0 && ', '}
          <strong>{v.name}:</strong> {v.value ?? '?'}
          {suffix}
        </span>
      ))}
      )
    </span>
  );
};

const renderLinkableAddress = (address?: string | null) => {
  if (!address) return null;
  return (
    <a
      href={`https://${address}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
    >
      {address}
      <i className="pi pi-external-link" style={{ fontSize: '0.85em' }} />
    </a>
  );
};

const renderMillimeters = (value?: number | null) => {
  if (value === null || value === undefined) return null;
  return `${value}mm`;
};

const renderDegrees = (value?: number | null) => {
  if (value === null || value === undefined) return null;
  return `${value}°`;
};

const renderPort = (port: number | null | undefined, defaultPort: number) => {
  if (port !== null && port !== undefined) return port;
  return (
    <span>
      {defaultPort} <span className="text-color-secondary font-italic">(default)</span>
    </span>
  );
};

const renderRelayOnThreshold = (value?: number | null) => {
  if (value === null || value === undefined) return 'no data';
  if (value === 0) return 'Off';
  if (value >= 1 && value <= 4) return `L${value}`;
  return `error (${value})`;
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : null;

const formatDateFull = (value?: string) =>
  value ? new Date(value).toLocaleString('sv-SE').replace('T', ' ') : null;

const SystemDetails = ({ mobileView, systemDetails, getSystemDetails }: SystemDetailsProps) => {
  const { systemID } = useParams<{ systemID: string }>();
  const history = useHistory();
  const currentSystemId = systemID ? parseInt(systemID, 10) : null;
  const isCurrentSystemLoaded = systemDetails != null && systemDetails.id === currentSystemId;

  const handleBack = () => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push('/systems-overview');
    }
  };

  useEffect(() => {
    if (systemID) {
      getSystemDetails(parseInt(systemID, 10));
    }
  }, [systemID, getSystemDetails]);

  const rows: Row[] = useMemo(() => {
    if (!systemDetails) return [];

    const isW2 = systemDetails.systemVersion === 'W2';

    return [
      { label: 'Name', value: systemDetails.name },
      { label: 'System version', value: systemDetails.systemVersion == 'W2' ? 'WSN' : 'LoRa' },
      ...(isW2 ? [{ label: 'External system ID', value: systemDetails.externalSystemId ?? null }] : []),
      ...(isW2 ? [{ label: 'Internal system ID', value: systemDetails.systemIdForRadioSet?.length ? systemDetails.systemIdForRadioSet.join(', ') : null }] : []),
      { label: 'Sync timestamp', value: formatDateFull(systemDetails.externalSynchronizationAt) },
      { label: 'Latitude', value: systemDetails.latitude ?? null },
      { label: 'Longitude', value: systemDetails.longitude ?? null },
      ...(isW2 ? [{ label: 'Public IP address', value: renderLinkableAddress(systemDetails.w2PublicIpAddress) }] : []),
      ...(isW2 ? [{ label: 'HTTP port', value: renderPort(systemDetails.w2HttpPort, DEFAULT_HTTP_PORT) }] : []),
      ...(isW2 ? [{ label: 'HTTPS port', value: renderPort(systemDetails.w2HttpsPort, DEFAULT_HTTPS_PORT) }] : []),
      ...(isW2 ? [{ label: 'SSH port', value: renderPort(systemDetails.w2SshPort, DEFAULT_SSH_PORT) }] : []),
      ...(isW2 ? [{ label: 'VPN2 IP', value: renderLinkableAddress(systemDetails.gateways?.[0]?.vpn2_ip) }] : []),
      ...(isW2 ? [{ label: 'Internet check host', value: systemDetails.internetCheckHost ?? null }] : []),
      ...(isW2
        ? [
            {
              label: 'Modbus TCP',
              value:
                systemDetails.w2ModbusEnabled === undefined || systemDetails.w2ModbusEnabled === null
                  ? null
                  : systemDetails.w2ModbusEnabled
                  ? 'On'
                  : 'Off',
            },
          ]
        : []),
      ...(isW2 ? [{ label: 'Relay on threshold', value: renderRelayOnThreshold(systemDetails.w2RelayOnThreshold) }] : []),
      {
        label: 'Battery level',
        value: renderLabeledSummary(systemDetails.batteryLevel?.min, systemDetails.batteryLevel?.values),
      },
      {
        label: 'Deflections',
        value: renderLabeledSummary(systemDetails.deflections?.max, systemDetails.deflections?.values),
      },
      { label: 'Obstruction detection threshold', value: renderMillimeters(systemDetails.obstructionDetectionThreshold) },
      { label: 'Obstruction change threshold', value: renderMillimeters(systemDetails.obstructionChangeThreshold) },
      { label: 'Obstruction cancel threshold', value: renderMillimeters(systemDetails.obstructionCancelThreshold) },
      { label: 'Inclinometer range', value: renderDegrees(systemDetails.inclinometerRange) },
      { label: 'Angle change threshold', value: renderDegrees(systemDetails.angleChangeThreshold) },
      { label: 'Buyer', value: systemDetails.buyerName ?? null },
      { label: 'Service purchaser', value: systemDetails.servicePurchaserName ?? null },
      { label: 'Guarantee valid until', value: formatDate(systemDetails.guaranteeAgreementEndDate) },
      { label: 'Handover date', value: formatDate(systemDetails.handoverDate) },
      {
        label: 'Base guarantee time',
        value:
          systemDetails.baseGuaranteeTime === null || systemDetails.baseGuaranteeTime === undefined
            ? null
            : `${systemDetails.baseGuaranteeTime} years`,
      },
      {
        label: 'Maintenance covered by guarantee',
        value:
          systemDetails.maintenanceCoveredByGuarantee === undefined
            ? null
            : systemDetails.maintenanceCoveredByGuarantee
            ? 'Yes'
            : 'No',
      },
    ];
  }, [systemDetails]);

  // device_sim_pin is read straight off the device's SIM (not user-entered) and is
  // the ground truth - flag sms_simcard_pin visually when it doesn't match, since
  // that means the number the installer has on record is wrong.
  const renderSimPinComparison = (inputPin?: string | null, devicePin?: string | null) => {
    if (!devicePin) return inputPin ?? null;
    if (!inputPin) return devicePin;
    const mismatch = devicePin !== inputPin;
    if (!mismatch) return devicePin;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
        {devicePin}
        <i
          className="pi pi-exclamation-triangle"
          style={{ color: '#c55' }}
          data-pr-tooltip={`Does not match entered SIM PIN (${inputPin})`}
          data-pr-position="top"
        />
      </span>
    );
  };

  // "p-component" pulls in the same font-family/font-size/color PrimeReact uses
  // for the DataTable cells above, so this text matches the rest of the page
  // instead of falling back to the (smaller) ambient body font.
  const panelStyle: React.CSSProperties = {
    flex: '1 1 300px',
    minWidth: '260px',
    backgroundColor: '#ffffff',
    color: '#4b5563',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
  };

  const sectionHeadingStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '0.85em',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    marginTop: '0.7rem',
    marginBottom: '0.3rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #eee',
  };

  // Fixed label column (instead of space-between, which pins the value to the
  // panel's far right edge) so label/value sit close together like the columns
  // in the table above, rather than spreading apart on wide desktop panels.
  const fieldRow = (label: string, value: React.ReactNode) => (
    <div key={label} style={{ display: 'flex', gap: '0.75rem', padding: '3px 0' }}>
      <span style={{ color: '#6c757d', flex: '0 0 150px' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value ?? ''}</span>
    </div>
  );

  const renderWebServerConfig = (config: GatewayDetails['web_server_config'] | null | undefined) => {
    const hasAnyData =
      !!config &&
      ((config.w2_cfg_dhcp !== null && config.w2_cfg_dhcp !== undefined) ||
        !!config.w2_cfg_ip ||
        !!config.w2_cfg_netmask ||
        !!config.w2_cfg_gateway ||
        !!config.w2_cfg_dns1 ||
        !!config.w2_cfg_dns2);

    if (!hasAnyData) {
      return 'no data';
    }

    return (
      <div>
        <div>
          <b>DHCP:</b>{' '}
          {config.w2_cfg_dhcp === null || config.w2_cfg_dhcp === undefined
            ? null
            : config.w2_cfg_dhcp
            ? 'Yes'
            : 'No'}
        </div>
        {/* This line doesn't wrap (each field stays nowrap so "IP: 1.2.3.4,"
            never breaks mid-value) - on narrow/mobile widths that's wider than
            the screen, so make it scroll horizontally within its own box
            instead of overflowing the page. */}
        <div style={{ overflowX: 'auto' }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            <b>IP:</b> {config.w2_cfg_ip},{' '}
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <b>mask:</b> {config.w2_cfg_netmask},{' '}
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <b>gateway:</b> {config.w2_cfg_gateway},{' '}
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <b>DNS:</b> {config.w2_cfg_dns1}, {config.w2_cfg_dns2}
          </span>
        </div>
      </div>
    );
  };

  // The SMS SIM lives on the device itself, the data SIM lives in the router
  // that's attached to it - so each SIM card is grouped under the hardware it
  // physically sits in, rather than both SIM cards being grouped together.
  const renderDevicePanel = (gateway: GatewayDetails, isW2: boolean) => {
    const hasSmsSim =
      !!gateway.sms_simcard_telephone ||
      !!gateway.sms_simcard_network ||
      !!gateway.sms_simcard_owner ||
      !!gateway.sms_simcard_pin ||
      !!gateway.sms_simcard_puk;
    return (
      <div className="p-component" style={panelStyle}>
        <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>SIM CARD SMS</div>
        {hasSmsSim ? (
          <>
            {fieldRow('Telephone', gateway.sms_simcard_telephone)}
            {fieldRow('Operator', gateway.sms_simcard_network)}
            {fieldRow('Owner', gateway.sms_simcard_owner)}
            {fieldRow('PIN', renderSimPinComparison(gateway.sms_simcard_pin, gateway.device_sim_pin))}
            {fieldRow('PUK', gateway.sms_simcard_puk)}
          </>
        ) : (
          'no data'
        )}
      </div>
    );
  };

  const renderRouterPanel = (gateway: GatewayDetails) => {
    const hasRouter = !!gateway.router_serial || !!gateway.router_owner || !!gateway.router_supplier;
    const hasDataSim =
      !!gateway.data_simcard_telephone ||
      !!gateway.data_simcard_network ||
      !!gateway.data_simcard_owner ||
      !!gateway.data_simcard_pin ||
      !!gateway.data_simcard_puk ||
      !!gateway.data_simcard_ip;
    return (
      <div className="p-component" style={panelStyle}>
        <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>ROUTER</div>
        {hasRouter ? (
          <>
            {fieldRow('Serial', gateway.router_serial)}
            {fieldRow('Owner', gateway.router_owner)}
            {fieldRow('Supplier', gateway.router_supplier)}
            <div style={sectionHeadingStyle}>SIM card DATA</div>
            {hasDataSim ? (
              <>
                {fieldRow('Telephone', gateway.data_simcard_telephone)}
                {fieldRow('Operator', gateway.data_simcard_network)}
                {fieldRow('Owner', gateway.data_simcard_owner)}
                {fieldRow('PIN', gateway.data_simcard_pin)}
                {fieldRow('PUK', gateway.data_simcard_puk)}
                {fieldRow('IP', gateway.data_simcard_ip)}
              </>
            ) : (
              'no data'
            )}
          </>
        ) : (
          'no data'
        )}
      </div>
    );
  };

  const buildGatewayRows = (gateway: GatewayDetails, isW2: boolean): Row[] => [
    { label: 'Firmware version', value: gateway.firmware_version ?? null },
    { label: 'Supply voltage', value: gateway.supply_voltage != null ? `${gateway.supply_voltage}V` : null },
    ...(isW2 ? [{ label: 'UC web server config', value: renderWebServerConfig(gateway.web_server_config) }] : []),
  ];

  if (!isCurrentSystemLoaded) {
    return <MapLoading testID="system-details-loading" />;
  }

  const gateways = systemDetails?.gateways ?? [];
  const title = systemDetails?.name ?? '';
  const labelColumnWidth = mobileView ? '45%' : '30%';

  return (
    <div>
      {systemDetails && (
        <>
          <Tooltip target="[data-pr-tooltip]" style={{ maxWidth: '300px' }} />
          <div className={mobileView ? 'flex flex-column gap-2 mb-3' : 'flex align-items-center gap-3 mb-3'}>
            <Button
              icon="pi pi-arrow-left"
              className="p-button-rounded p-button-outlined align-self-start"
              onClick={handleBack}
              aria-label="Back to systems overview"
            />
            <h2
              className={`system-details-title m-0 ${mobileView ? 'text-xl' : ''}`}
              style={{
                fontWeight: 700,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: mobileView ? 'normal' : 'nowrap',
                wordBreak: mobileView ? 'break-word' : 'normal',
              }}
              data-pr-tooltip={mobileView ? undefined : title}
            >
              {title}
            </h2>
          </div>
        </>
      )}

      <DataTable
        value={rows}
        className="p-datatable-sm"
        showGridlines
        showHeaders={false}
        stripedRows
      >
        <Column
          field="label"
          body={(row: Row) => <span style={{ fontWeight: 600 }}>{row.label}</span>}
          style={{ width: labelColumnWidth }}
        />
        <Column field="value" body={(row: Row) => row.value} />
      </DataTable>
      {gateways.map((gateway, index) => {
        const isW2 = systemDetails?.systemVersion === 'W2';
        return (
          <div key={gateway.node_id} className="mt-4">
            {gateways.length > 1 && (
              <h3 className="mb-2" style={{ fontWeight: 700 }}>
                Gateway {index + 1} (Node {gateway.node_id})
              </h3>
            )}
            {gateways.length === 1 && (
              <h3 className="mb-2" style={{ fontWeight: 700 }}>
                Gateway
              </h3>
            )}
            <DataTable
              value={buildGatewayRows(gateway, isW2)}
              className="p-datatable-sm"
              showGridlines
              showHeaders={false}
              stripedRows
              tableStyle={{ tableLayout: 'fixed', width: '100%' }}
            >
              <Column
                field="label"
                body={(row: Row) => <span style={{ fontWeight: 600 }}>{row.label}</span>}
                style={{ width: labelColumnWidth }}
              />
              <Column field="value" body={(row: Row) => row.value} />
            </DataTable>
            <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {renderDevicePanel(gateway, isW2)}
              {renderRouterPanel(gateway)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    mobileView: state.dashboard.mobileView,
    systemDetails: state.installer.systemDetails,
  };
};

const actions = {
  getSystemDetails,
};

export default connect(mapStateToProps, actions)(SystemDetails);